import React, { useEffect, useState } from 'react';
import { BookOpen, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';
import { logger } from '../../lib/logger';

const pct = (v, digits = 1) => (v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${Number(v).toFixed(digits)}%`);
const dd = (v) => (v === null || v === undefined ? '—' : v > 0 ? `-${v}%` : '0%');
const rate = (v) => (v === null || v === undefined ? '—' : `${v}%`);
const tone = (v) => (v === null || v === undefined ? 'text-slate-400' : v > 0 ? 'text-emerald-300' : v < 0 ? 'text-rose-300' : 'text-slate-200');

const Spark = ({ points, testid }) => {
    if (!points || points.length < 2) return <div className="h-10 text-[10px] text-slate-500 flex items-center">henüz kapanmış işlem yok</div>;
    const ys = points.map((p) => p.equity);
    const min = Math.min(...ys), max = Math.max(...ys), span = max - min || 1;
    const d = ys.map((y, i) => `${(i / (ys.length - 1)) * 100},${40 - ((y - min) / span) * 36 - 2}`).join(' ');
    const up = ys[ys.length - 1] >= ys[0];
    return (
        <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="h-10 w-full" data-testid={testid}>
            <polyline points={d} fill="none" stroke={up ? '#34d399' : '#fb7185'} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        </svg>
    );
};

const Block = ({ title, c, testid }) => (
    <div className="rounded-md border border-slate-700 bg-slate-900/50 p-2.5 space-y-1.5" data-testid={testid}>
        <div className="flex items-baseline justify-between">
            <span className="text-[10px] uppercase tracking-wider text-slate-400">{title}</span>
            <span className="text-[10px] text-slate-500">{c.n} kapanış</span>
        </div>
        <div className="flex items-baseline gap-2">
            <span className={`font-mono text-lg font-bold ${tone(c.return_pct)}`}>{c.final_equity.toLocaleString('en-US', { maximumFractionDigits: 0 })}$</span>
            <span className={`font-mono text-xs ${tone(c.return_pct)}`}>{pct(c.return_pct)}</span>
        </div>
        <Spark points={c.points} testid={`${testid}-spark`} />
        <div className="grid grid-cols-3 gap-x-2 gap-y-1 text-[10px]">
            <span className="text-slate-400">Max düşüş</span><span className="col-span-2 font-mono text-rose-300">{dd(c.max_dd_pct)}</span>
            <span className="text-slate-400">İsabet</span><span className="col-span-2 font-mono text-slate-200">{rate(c.win_rate)}</span>
            <span className="text-slate-400">Kâr faktörü</span><span className="col-span-2 font-mono text-slate-200">{c.profit_factor ?? '—'}</span>
            <span className="text-slate-400">Beklenti</span><span className="col-span-2 font-mono text-slate-200">{c.expectancy_r === null || c.expectancy_r === undefined ? '—' : `${c.expectancy_r > 0 ? '+' : ''}${c.expectancy_r}R / işlem`}</span>
        </div>
    </div>
);

export default function PaperLedgerCard() {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(30);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let alive = true;
        setErr(null);
        apiClient.get(`/predictions/ledger?days=${days}`)
            .then((r) => alive && setData(r.data))
            .catch((e) => { logger.error('ledger load failed', e); alive && setErr('Defter yüklenemedi'); });
        return () => { alive = false; };
    }, [days]);

    return (
        <div className="rounded-lg border border-slate-700 bg-slate-800/40 overflow-hidden" data-testid="paper-ledger-card">
            <div className="px-3 py-2 border-b border-slate-700 flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-sky-300" />
                <span className="text-sm font-semibold text-slate-100">Kâğıt Defter</span>
                <span className="text-[10px] text-slate-400">{data ? `${data.account.toLocaleString('en-US')}$ · %${data.risk_pct} risk/işlem` : ''}</span>
                <div className="ml-auto flex gap-1">
                    {[7, 30, 90].map((d) => (
                        <button key={d} type="button" onClick={() => setDays(d)} data-testid={`ledger-days-${d}`}
                            className={`text-[10px] px-2 py-0.5 rounded-sm border ${days === d ? 'border-sky-400/60 bg-sky-500/15 text-sky-100' : 'border-slate-700 text-slate-400 hover:text-slate-200'}`}>
                            {d}g
                        </button>
                    ))}
                </div>
            </div>
            {!data && !err && <div className="p-4 text-xs text-slate-400 flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />Hesaplanıyor…</div>}
            {err && <div className="p-4 text-xs text-rose-300" data-testid="ledger-error">{err}</div>}
            {data && (
                <div className="p-3 space-y-3">
                    <p className="text-[10px] text-slate-400 leading-snug" data-testid="ledger-note">{data.note_tr}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Block title="Resmi sinyaller" c={data.system} testid="ledger-system" />
                        <Block title="Radar planları" c={data.plans} testid="ledger-plans" />
                    </div>
                    <div className="space-y-1" data-testid="ledger-variants">
                        <div className="text-[10px] uppercase tracking-wider text-sky-200 font-semibold">Gölge test — kurallar sıkılaşsa ne olurdu?</div>
                        <table className="w-full text-[10px]">
                            <thead className="text-slate-500"><tr><th className="text-left font-normal py-0.5">Kural</th><th className="text-right font-normal">N</th><th className="text-right font-normal">Getiri</th><th className="text-right font-normal">Max DD</th><th className="text-right font-normal">İsabet</th><th className="text-right font-normal">Beklenti</th></tr></thead>
                            <tbody>
                                {data.variants.map((v) => (
                                    <tr key={v.label} className="border-t border-slate-800" data-testid={`ledger-variant-${v.label}`}>
                                        <td className="py-0.5 text-slate-300">{v.label}</td>
                                        <td className="text-right font-mono text-slate-400">{v.n}</td>
                                        <td className={`text-right font-mono ${tone(v.return_pct)}`}>{pct(v.return_pct)}</td>
                                        <td className="text-right font-mono text-rose-300">{dd(v.max_dd_pct)}</td>
                                        <td className="text-right font-mono text-slate-300">{rate(v.win_rate)}</td>
                                        <td className="text-right font-mono text-slate-300">{v.expectancy_r === null || v.expectancy_r === undefined ? '—' : `${v.expectancy_r > 0 ? '+' : ''}${v.expectancy_r}R`}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
